import classNames from "classnames"
import React from "react"
import { currencyFormatWithSign } from "scripts/utils/currencyFormat"
import type { AppliedCertificate } from "scripts/types"
import type {
  TotalRowProps,
  TotalsProps,
  TotalsLabels,
  ServicesMap,
  RawService,
  ExtendedTotalsType,
} from "./Totals.types"

/**
 * Renders a single row in the totals section displaying a total value with optional icon, label, count, and formatting.
 *
 * @param props - The component props
 * @returns The rendered row element, or null if value is undefined or 0
 *
 * @example
 * ```tsx
 * <TotalRow
 *   iconClass="bag-icon"
 *   label="Products"
 *   count={5}
 *   value={1500}
 *   currencySign="₴"
 * />
 * ```
 */
function TotalRow({
  className = "",
  iconClass,
  label,
  count,
  value,
  isDiscount,
  isCashback,
  valueClassName = "",
  titleClassName = "",
  currencySign = "₴",
}: TotalRowProps): JSX.Element | null {
  const liClass = classNames("flex-c-b total-content__li", className, {
    "total-content__li_discount": isDiscount,
    "total-content__li_green-num": isCashback,
  })

  if (value === undefined || value === 0) {
    return null
  }

  let showValue: string | null

  switch (true) {
    case typeof value === "string":
      showValue = value
      break
    case typeof value === "number":
      showValue = currencyFormatWithSign(value, currencySign)
      break
    case value === null:
      showValue = null
      break
    default:
      showValue = null
  }

  return (
    <li className={liClass}>
      <div className={`title flex-c-b ${titleClassName}`}>
        {iconClass ? <div className={`${iconClass} icon`} /> : null}
        <span className="text">
          {label}
          {count ? <b className="b">{count}</b> : null}
        </span>
      </div>
      <div className={`num body-r-14 ${valueClassName}`}>
        {isDiscount && showValue ? "- " : null}
        {isCashback && showValue ? "+ " : null}
        <span>{showValue}</span>
      </div>
    </li>
  )
}

/**
 * Transforms raw service data into a standardized object format for rendering.
 * Handles both the new services array format and the legacy flat structure.
 *
 * @param totalsProps - The totals object from the discount data
 * @param labels - The labels object containing service display names
 * @returns A map of service type keys to normalized service items
 *
 * @example
 * ```ts
 * // New format input
 * const totals = {
 *   services: [{ type: 'exchange', title: 'Exchange', price: 100, qty: 1 }]
 * }
 * const result = normalizeServiceObject(totals, labels)
 * // result: { exchange: { label: 'Exchange', sum: 100, count: 1 } }
 * ```
 */
function normalizeServiceObject(
  totalsProps: ExtendedTotalsType | undefined,
  labels: TotalsLabels,
): ServicesMap {
  if (totalsProps?.services && Array.isArray(totalsProps.services)) {
    const services: ServicesMap = {}
    totalsProps.services.forEach(({ type, title, price, qty }: RawService) => {
      services[type] = {
        label: title,
        sum: price,
        count: qty,
      }
    })
    return services
  }

  // Fallback to old/legacy structure. Remove when old structure is deprecated.
  return {
    exchange: {
      label: labels.services.exchange.name,
      sum: totalsProps?.services_amount ?? 0,
      count: totalsProps?.services_count,
    },
    unlimitedcare: {
      label: labels.services.unlimitedcare.name,
      sum: totalsProps?.services_amount_unlimited_care ?? 0,
      count: totalsProps?.services_count_unlimited_care,
    },
  }
}

/**
 * Renders the totals section in the cart modal, displaying various calculated amounts
 * such as total, discounts, bonuses, services, delivery, and cashback.
 *
 * This component aggregates and displays all pricing information from the cart including:
 * - Total sum without discount
 * - Applied services (exchange, unlimited care, etc.)
 * - Promocode discounts
 * - User bonuses and promotional bonuses
 * - Gift certificates
 * - Personal and birthday discounts
 * - Marketing discounts
 * - Delivery costs
 * - Cashback earned
 *
 * @param props - The component props
 * @returns The rendered totals section
 *
 * @example
 * ```tsx
 * <Totals
 *   globalCart={globalCart}
 *   labels={{
 *     total: "Products",
 *     cashback: "Cashback",
 *     // ... other labels
 *   }}
 * />
 * ```
 */
export default function Totals(props: TotalsProps): JSX.Element {
  // Merge window labels with prop labels (prop labels take precedence)
  const labels = {
    ...(window.labels?.checkout || {}),
    ...props.labels,
  }

  const currencySign = props.globalCart.discount.currency_icon || "₴"

  const discountTotals: ExtendedTotalsType =
    props.globalCart.discount.totals || ({} as ExtendedTotalsType)

  // Destructure with defaults from discountTotals
  // Props can override any of these values
  const {
    totalWod = {
      label: labels.total,
      sum: discountTotals.total_sum_without_discount || null,
      count: props.globalCart.cartItems.length,
    },
    services = normalizeServiceObject(props.globalCart.discount.totals, labels),
    promocodeDiscount = {
      label: labels.promocode_discount,
      sum: discountTotals.promocode_discount,
    },
    appliedBonus = {
      label: labels.my_bonus,
      sum: discountTotals.applied_bonus,
    },
    appliedPromoBonus = {
      label: labels.action_bonus,
      sum: discountTotals.applied_promo_bonus,
    },
    appliedCertificates = {
      label: labels.gift_certificate,
      sum: discountTotals.applied_certificates,
    },
    personalDiscount = {
      label: labels.personal_discount,
      sum: discountTotals.personal_discount,
    },
    personalBirthdayDiscount = {
      label: labels.personal_birthday_discount,
      sum: discountTotals.personal_birthday_discount,
    },
    marketingDiscount = {
      label: labels.marketing_discount,
      sum: discountTotals.marketing_discount,
    },
    deliveryPrice = {
      label: labels.productDelivery,
      sum: discountTotals.delivery_amount,
    },
    cashback = {
      label: labels.cashback,
      sum: discountTotals.cashback,
    },
  } = props

  return (
    <div className="total-content">
      <ul>
        {/* Total without discount */}
        <TotalRow
          key="total-without-discount"
          className="total-content__li_total"
          iconClass="bag-icon"
          label={totalWod.label}
          count={totalWod.count}
          value={totalWod.sum}
          currencySign={currencySign}
        />
        {/* Services (exchange, unlimited care, etc.) */}
        {Object.entries(services).map(([key, service]) => (
          <TotalRow
            key={key}
            className="total-content__li_total"
            iconClass={`${key}-icon`}
            label={service.label}
            count={service.count}
            value={service.sum}
            currencySign={currencySign}
          />
        ))}
        {/* Promocode discount */}
        <TotalRow
          key="promocode-discount"
          isDiscount
          iconClass="promocode-icon"
          label={promocodeDiscount.label}
          value={promocodeDiscount.sum}
          currencySign={currencySign}
        />
        {/* Applied bonuses */}
        <TotalRow
          key="applied-bonus"
          isDiscount
          valueClassName="bonus__marked"
          iconClass="bonus-icon"
          label={appliedBonus.label}
          value={appliedBonus.sum}
          currencySign={currencySign}
        />
        {/* Applied promo bonuses */}
        <TotalRow
          key="applied-promo-bonus"
          isDiscount
          valueClassName="bonus__marked"
          iconClass="label-icon"
          label={appliedPromoBonus.label}
          value={appliedPromoBonus.sum}
          currencySign={currencySign}
        />
        {/* Applied certificates */}
        {appliedCertificates.sum?.map?.((item: AppliedCertificate) => (
          <TotalRow
            key={item.number}
            isDiscount
            iconClass="certificate-icon"
            label={appliedCertificates.label}
            value={item.amountApplied}
            currencySign={currencySign}
          />
        ))}
        {/* Personal discount */}
        <TotalRow
          key="personal-discount"
          isDiscount
          valueClassName=""
          iconClass="discount-icon"
          label={personalDiscount.label}
          value={personalDiscount.sum}
          currencySign={currencySign}
        />
        {/* {basket.totals.amount_promo_bonus > 0 ? (
          <li className="flex-c-b">
            <div className="title flex-c-b">
              <div className="action-icon icon" />
              {window.labels.checkout.amount_promo_bonus}
            </div>
            <div className="num body-r-14">
              -{" "}
              <span>
                {basket.totals.amount_promo_bonus_formatted}
              </span>
            </div>
          </li>
        ) : (
          ""
        )} */}
        {/* Personal birthday discount */}
        <TotalRow
          key="personal-birthday-discount"
          isDiscount
          iconClass="calendar-icon"
          label={personalBirthdayDiscount.label}
          value={personalBirthdayDiscount.sum}
          currencySign={currencySign}
        />
        {/* Marketing discount */}
        <TotalRow
          key="marketing-discount"
          isDiscount
          iconClass="calendar-icon"
          label={marketingDiscount.label}
          value={marketingDiscount.sum}
          currencySign={currencySign}
        />
        {/* Delivery price */}
        <TotalRow
          key="delivery-price"
          label={deliveryPrice.label}
          value={deliveryPrice.sum}
          currencySign={currencySign}
          className="total-content__li_divider-bottom"
        />
        {/* Cashback earned */}
        <TotalRow
          key="cashback"
          isCashback
          valueClassName="total-content__li_green-num"
          label={cashback.label}
          value={cashback.sum}
          currencySign={currencySign}
        />
      </ul>
    </div>
  )
}
